import React, { useEffect, useState } from "react";
import { Eye, EyeOff, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const chargeAmount = (c, sub) => (c.kind === "percent"
  ? (Number(sub || 0) * Number(c.value || 0)) / 100
  : Number(c.value || 0));

/** Compact "Additional charges" block: nothing is shown until a charge is added. */
export const ChargesSection = ({ kind, form, setForm, subtotal, taxRates = [0, 5, 12, 18, 28] }) => {
  const charges = form.charges || [];
  const [catalogue, setCatalogue] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [thinking, setThinking] = useState(false);

  useEffect(() => {
    api.get("/charge-catalogue").then((r) => setCatalogue(r.data)).catch(() => {});
  }, []);

  const set = (list) => setForm((f) => ({ ...f, charges: list }));
  const edit = (i, patch) => set(charges.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const add = (name) => {
    const known = catalogue.find((c) => c.name === name);
    if (charges.some((c) => c.name === name)) return toast.info("That charge is already on this document.");
    set([...charges, {
      name, kind: known?.kind || "fixed", value: known?.value || 0,
      taxable: known?.taxable ?? true, tax_rate: known?.tax_rate ?? 18,
      remarks: "", show_on_print: true, cost_amount: "",
    }]);
  };

  const suggest = async () => {
    setThinking(true);
    try {
      const { data } = await api.post("/charge-suggest", {
        kind, lead_id: form.lead_id || "", customer_name: form.customer_name || "",
        subtotal: Number(subtotal || 0),
        items: (form.items || []).filter((i) => i.name).map((i) => ({
          name: i.name, qty: Number(i.qty || 0), price: Number(i.price || 0),
          tax_rate: Number(i.tax_rate || 0),
        })),
      });
      setSuggestions(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setThinking(false); }
  };

  const accept = (s) => {
    if (charges.some((c) => c.name === s.name)) return toast.info("Already added.");
    set([...charges, {
      name: s.name, kind: s.kind === "percent" ? "percent" : "fixed", value: Number(s.value || 0),
      taxable: s.taxable !== false, tax_rate: Number(s.tax_rate ?? 18),
      remarks: s.reason || "", show_on_print: true, cost_amount: "",
    }]);
    setSuggestions((v) => ({ ...v, suggestions: v.suggestions.filter((x) => x.name !== s.name) }));
  };

  const total = charges.reduce((a, c) => a + chargeAmount(c, subtotal), 0);

  return (
    <section className="space-y-3" data-testid="charges-section">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display text-sm font-semibold">Additional charges</h3>
        {charges.length > 0 && (
          <span className="num text-xs text-muted-foreground" data-testid="charges-total">{money(total)}</span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          <Select value="" onValueChange={add}>
            <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="charge-picker">
              <SelectValue placeholder="Add charge" />
            </SelectTrigger>
            <SelectContent>
              {catalogue.filter((c) => !charges.some((x) => x.name === c.name)).map((c) => (
                <SelectItem key={c.name} value={c.name}>
                  {c.name}{c.count > 0 ? ` (${c.count})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" size="sm" variant="soft" disabled={thinking} data-testid="charge-suggest-btn"
            onClick={suggest}>
            {thinking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Suggest
          </Button>
        </div>
      </div>

      {suggestions && (
        <div className="space-y-2 rounded-xl border border-primary/25 bg-primary-soft/40 p-3" data-testid="charge-suggestions">
          <p className="text-xs text-muted-foreground">
            {suggestions.note || "Suggested from this customer's history and what you usually charge."}
            {" "}Nothing is added until you accept it.
          </p>
          {(suggestions.suggestions || []).map((s) => (
            <div key={s.name} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5">
              <span className="text-sm font-medium">{s.name}</span>
              <span className="num text-xs text-muted-foreground">
                {s.kind === "percent" ? `${s.value}% of subtotal` : money(s.value)}
                {s.taxable === false ? " \u00b7 no tax" : ` \u00b7 ${s.tax_rate}% GST`}
              </span>
              <span className="min-w-0 flex-1 truncate text-xs italic text-muted-foreground">{s.reason}</span>
              <Button type="button" size="sm" variant="outline" data-testid={`accept-charge-${s.name}`}
                onClick={() => accept(s)}>
                <Plus className="h-3.5 w-3.5" /> Add
              </Button>
            </div>
          ))}
          {(suggestions.suggestions || []).length === 0 && (
            <p className="text-xs text-muted-foreground">Nothing left to add.</p>
          )}
          <Button type="button" size="sm" variant="ghost" data-testid="charge-suggestions-dismiss"
            onClick={() => setSuggestions(null)}>Dismiss</Button>
        </div>
      )}

      {charges.map((c, i) => (
        <div key={i} className="grid gap-2 rounded-xl border border-border p-2.5 sm:grid-cols-12"
          data-testid={`charge-row-${i}`}>
          <Input className="sm:col-span-3" value={c.name} data-testid={`charge-name-${i}`}
            onChange={(e) => edit(i, { name: e.target.value })} placeholder="Charge name" />
          <Select value={c.kind} onValueChange={(v) => edit(i, { kind: v })}>
            <SelectTrigger className="sm:col-span-2" data-testid={`charge-kind-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed">Fixed amount</SelectItem>
              <SelectItem value="percent">% of subtotal</SelectItem>
            </SelectContent>
          </Select>
          <Input className="sm:col-span-1" type="number" min="0" step="0.01" value={c.value}
            data-testid={`charge-value-${i}`} onChange={(e) => edit(i, { value: e.target.value })} />
          <label className="flex items-center gap-1.5 text-xs font-medium sm:col-span-2">
            <Checkbox checked={c.taxable !== false} data-testid={`charge-taxable-${i}`}
              onCheckedChange={(v) => edit(i, { taxable: !!v, tax_rate: v ? (c.tax_rate || 18) : 0 })} />
            Taxable
          </label>
          <Select value={String(c.tax_rate ?? 18)} disabled={c.taxable === false}
            onValueChange={(v) => edit(i, { tax_rate: Number(v) })}>
            <SelectTrigger className="sm:col-span-2" data-testid={`charge-tax-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {taxRates.map((t) => <SelectItem key={t} value={String(t)}>{t}% GST</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center justify-end gap-1 sm:col-span-2">
            <span className="num mr-auto text-xs font-semibold sm:mr-1" data-testid={`charge-amount-${i}`}>{money(chargeAmount(c, subtotal))}</span>
            <Button type="button" size="iconSm" variant="ghost" data-testid={`charge-print-${i}`}
              title={c.show_on_print === false ? "Hidden from the customer copy" : "Shown on the customer copy"}
              onClick={() => edit(i, { show_on_print: c.show_on_print === false })}>
              {c.show_on_print === false ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button type="button" size="iconSm" variant="ghost" className="text-destructive"
              data-testid={`charge-remove-${i}`} onClick={() => set(charges.filter((_, j) => j !== i))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <Input className="sm:col-span-8" value={c.remarks || ""} data-testid={`charge-remarks-${i}`}
            onChange={(e) => edit(i, { remarks: e.target.value })}
            placeholder="Internal remarks - never printed" />
          <Input className="sm:col-span-4" type="number" min="0" value={c.cost_amount ?? ""}
            data-testid={`charge-cost-${i}`} onChange={(e) => edit(i, { cost_amount: e.target.value })}
            placeholder="What it costs us (for profit)" />
        </div>
      ))}
    </section>
  );
};
